import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DestinationPicker, needsWiderConfirm } from "@/components/post/DestinationPicker";
import { WiderAudienceDialog } from "@/components/post/WiderAudienceDialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Gestão de destinos de uma publicação da própria autora: acrescentar exige permissão (e confirmação se amplia a audiência); retirar nunca apaga os restantes.
export function ManageDestinationsDialog({ post, open, onOpenChange, onSaved }) {
  const [dests, setDests] = useState({ profile: false, feed: false, communities: [] });
  const [comms, setComms] = useState([]);
  const [confirmPriv, setConfirmPriv] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (post) setDests({ profile: !!post.destinations.profile, feed: !!post.destinations.feed, communities: post.destinations.communities.filter((c) => c.status !== "deleted" && c.status !== "missing").map((c) => c.id) });
  }, [post]);
  if (!post) return null;
  const cur = post.destinations;
  const widened = (dests.profile && !cur.profile) || (dests.feed && !cur.feed) || dests.communities.some((id) => !cur.communities.some((c) => c.id === id && c.status !== "deleted"));
  const save = async (confirm = false) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/profile/posts/${post.id}/destinations`, { ...dests, confirm_wider_audience: confirm });
      const pend = (data.added || []).filter((a) => a.status === "pending_approval").map((a) => a.name);
      toast.success(pend.length ? `Destinos atualizados · ${pend.join(", ")}: a aguardar aprovação` : "Destinos atualizados");
      onSaved?.(data); onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "CONFIRM_WIDER_AUDIENCE") setConfirmPriv(d.private_communities || []);
      else toast.error(formatApiError(d) || "Erro ao atualizar destinos");
    } finally { setBusy(false); }
  };
  const submit = () => {
    if (!dests.profile && !dests.feed && dests.communities.length === 0) { toast.error("Escolhe pelo menos um destino ou apaga a publicação"); return; }
    const priv = widened ? needsWiderConfirm(dests, comms) : null;
    if (priv) { setConfirmPriv(priv.map((c) => c.name)); return; }
    save(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="rounded-2xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="manage-destinations-dialog">
        <DialogHeader>
          <DialogTitle>Gerir destinos</DialogTitle>
          <DialogDescription>Acrescentar um destino partilha apenas o teu conteúdo (sem comentários ou reações de outros contextos). Retirar um destino mantém a publicação nos restantes.</DialogDescription>
        </DialogHeader>
        <DestinationPicker value={dests} onChange={setDests} onLoaded={setComms} />
        {cur.communities.some((c) => c.status === "hidden" || (c.status === "deleted")) && (
          <p className="text-[11px] text-zinc-500" data-testid="manage-destinations-note">Entradas retiradas pela moderação numa comunidade não podem ser repostas a partir daqui.</p>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={() => onOpenChange(false)} disabled={busy} data-testid="manage-destinations-cancel" className="px-3 py-1.5 rounded-xl border border-zinc-300 text-sm font-semibold">Cancelar</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="manage-destinations-save" className="px-4 py-1.5 rounded-xl bg-[#c8102e] text-white text-sm font-semibold disabled:opacity-40">{busy ? "A guardar…" : "Guardar"}</button>
        </div>
        <WiderAudienceDialog privateNames={confirmPriv} confirmLabel="Confirmar e guardar" onCancel={() => setConfirmPriv(null)} onConfirm={() => { setConfirmPriv(null); save(true); }} />
      </DialogContent>
    </Dialog>
  );
}
